import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Button,
  withField,
  ArrayField,
  Form,
  Dropdown,
  Toast,
  Typography,
  Tooltip,
  Tag,
  UserGuide,
} from "@douyinfe/semi-ui";
import { getLocalStorage, setLocalStorage } from "@utils/localstorage";
import { setIsJobSubmited } from "@state/global/reducer";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@state/store";
import { isEqual, size } from "lodash";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { ReactComponent as DeleteIcon } from "@assets/icons/delete.svg";
import { ReactComponent as MinimizeIcon } from "@assets/icons/minimize.svg";
import { ReactComponent as MaximizeIcon } from "@assets/icons/maximize.svg";
import { IconPlus, IconSearch, IconMore } from "@douyinfe/semi-icons";
import { ReactComponent as BulbIcon } from "@assets/icons/bulb_icon.svg";
import { ccdCodeValidate } from "@utils/validator";
import { getUserInfoApi } from "@services/user/user.api";
import { VhhDesignTypeEnum } from "@services/home/enum/home.enum";
import { debounce } from "lodash";
import type {
  BaseFormApi as FormApi,
  FormState,
} from "@douyinfe/semi-foundation/lib/es/form/interface";
import {
  DockingAttachementModel,
  SequenceModel,
  DockingPayloadModel,
  TokenInfoModel,
  JobModel,
  SequenceRowModel,
  JobSubmitInfoModel,
  ProvideStructureDataModel,
  ConstraintModel,
} from "@services/home/model/home.model";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { replaceWhitespace } from "@utils/common.util";
import {
  createDockingJobApi,
  getDockingTokenInfoApi,
} from "@services/home/home.api";
import type { StepItem } from "@douyinfe/semi-ui/lib/es/userGuide";
import {
  getChainIds,
  processDockingFasta,
  getProvideResidueMap,
  parseCCDSequence,
  scrollToLine,
  getEditRowIndex,
  getDockingHotspotResidueMap,
} from "@utils/data-process.util";
import JobLoginDialog from "../job-login-dialog";
import DockingAdvancedSettingField from "../docking-advanced-setting-field";
import DockingProvideStructureDialog from "../../components/docking-provide-structure-dialog";
import styles from "./index.module.scss";

const { TextArea, InputNumber } = Form;

const { Text } = Typography;

interface Prop {
  value?: DockingAttachementModel;
  exampleJobs: JobModel[];
  onChange?: (values: any) => void;
}

const DockingTableField = forwardRef<any, Prop>(
  ({ value, exampleJobs, onChange }, ref) => {
    const { t, i18n } = useTranslation();

    const dispatch = useDispatch<AppDispatch>();
    const formRef = useRef<FormApi<DockingAttachementModel>>();
    const navigate = useNavigate();

    const constraintRef = useRef<{
      validate(): Promise<boolean>;
      getValues(): ConstraintModel[] | null;
      openPopover(): void;
    }>();

    const inputRefs = useRef<Record<string, HTMLTextAreaElement>>({});
    const [tokenInfo, setTokenInfo] = useState<TokenInfoModel>();
    const initSequencesData = [{ copies: 1, sequence: "", isEdit: true }];

    const [isDeepThink, setIsDeepThink] = useState(false);
    const [loginVisible, setLoginVisible] = useState(false);
    const [currentValue, setCurrentValue] = useState<DockingAttachementModel>();
    const [loadingInfo, setLoadingInfo] = useState(false);
    const [jobParameter, setJobParameter] = useState<DockingPayloadModel>();

    const [userType, setUserType] = useState("");

    const [username, setUsername] = useState<string>();

    const [loading, setLoading] = useState(false);

    const [constraintsValid, setConstraintsValid] = useState(true);

    const [provideDialogVisible, setProvideDialogVisible] = useState(false);

    const [editedRow, setEditedRow] = useState<SequenceRowModel>();

    const [editedIndex, setEditedIndex] = useState<number>();
    const [visible, setVisible] = useState(false);
    const [current, setCurrent] = useState(0);
    const [guideSteps, setGuideSteps] = useState<StepItem[]>([]);

    const showDialog = () => {
      setVisible(true);
    };

    function onSeqBlur(field: string) {
      if (!formRef.current) {
        return;
      }
      const target = formRef.current.getValue(field as any) as SequenceRowModel;
      const p = ccdCodeValidate(target.sequence);
      p.then((hasError) => {
        if (!hasError) {
          formRef.current?.setValue(`${field}.isEdit` as any, false);
        }
      });
    }

    function onEditSeq(
      e: React.PointerEvent<HTMLDivElement>,
      key: string,
      field: string,
    ) {
      if (!formRef.current || visible) {
        return;
      }

      let targetIndex = -1;
      let clickedRowIndex = -1;

      const targetSpanDom = e.target as HTMLSpanElement;
      const seqContainerDom = targetSpanDom?.closest(
        ".seq-wrapper",
      ) as HTMLDivElement;

      if (targetSpanDom && seqContainerDom) {
        const seqsDom = seqContainerDom.querySelectorAll(".seq");
        targetIndex = Array.from(seqsDom).findIndex((dom) => {
          return dom === targetSpanDom;
        });

        clickedRowIndex = getEditRowIndex(
          seqsDom as NodeListOf<HTMLSpanElement>,
          targetSpanDom,
        );
      }
      const target = formRef.current.getValue(field as any) as SequenceRowModel;
      if (!target.isEdit) {
        formRef.current.setValue(`${field}.isEdit` as any, true);
      }

      setTimeout(() => {
        const textDom = inputRefs.current[key];
        const val = textDom.value;
        textDom.focus();
        if (targetIndex === -1) {
          textDom.setSelectionRange(val.length, val.length);
        } else {
          if (clickedRowIndex !== -1) {
            scrollToLine(textDom, clickedRowIndex);
          }
          textDom.setSelectionRange(targetIndex + 1, targetIndex + 1);
        }
      }, 0);
    }

    function setValues(p: DockingAttachementModel, turbo: boolean) {
      if (onChange) {
        onChange({
          ...p,
          turbo,
        });
      }
    }

    function getTokenInfo(p: DockingPayloadModel) {
      setLoadingInfo(true);
      getDockingTokenInfoApi(p)
        .then((data) => {
          setTokenInfo(data);
          setLoadingInfo(false);
        })
        .catch(() => {
          setTokenInfo(undefined);
          setLoadingInfo(false);
        });
    }

    async function submitJob() {
      if (!formRef.current) {
        return;
      }

      try {
        const values = await formRef.current.validate();

        const sequences = values.sequences.map((item, i) => {
          const chianIds = getChainIds(values.sequences, i, item.copies);
          const o: SequenceModel = {
            chain_ids: chianIds.split(","),
            sequence: replaceWhitespace(item.sequence),
            struct_condition_fid: item.struct_condition_fid,
            struct_condition_items: item.struct_condition_items,
            pdb_input_type: item.pdb_input_type,
            pdb_input_value: item.pdb_input_value,
          };

          return o;
        });

        const haveConstraint = (values.constraints || []).some((item) => {
          return Object.keys(item).length > 0;
        });

        const params: DockingPayloadModel = {
          // job_name: "",
          turbo: isDeepThink,
          sequences,
          constraints: haveConstraint ? values.constraints : null,
        };

        if (!constraintsValid) {
          return;
        }

        const constraints = constraintRef.current?.getValues();

        if (constraints && constraints.length > 0) {
          const haveInput = constraints.some((item) => {
            return Object.keys(item).length > 0;
          });

          if (haveInput) {
            const isValid = await constraintRef.current?.validate();

            if (!isValid) {
              setConstraintsValid(false);
              return;
            }
          }
        }

        const targetJob = exampleJobs.find((item) => {
          const p = item.parameters as DockingPayloadModel;
          const seqs = p.sequences.map((seq) => {
            const o: SequenceModel = {
              chain_ids: seq.chain_ids,
              sequence: seq.sequence,
              struct_condition_fid: seq.struct_condition_fid,
              struct_condition_items: seq.struct_condition_items,
              pdb_input_type: seq.pdb_input_type,
              pdb_input_value: seq.pdb_input_value,
            };

            return o;
          });

          const currentParams: DockingPayloadModel = {
            turbo: p.turbo,
            sequences: seqs,
            constraints: p.constraints,
          };
          const isSame = isEqual(params, currentParams);

          return isSame;
        });

        if (targetJob) {
          navigate(`/job-detail/${targetJob.id}`);
          const jobSubmitedInfo =
            getLocalStorage<JobSubmitInfoModel>("is_job_submited");
          if (!jobSubmitedInfo) {
            dispatch(setIsJobSubmited(true));
            setLocalStorage("is_job_submited", {
              submited: true,
            } as JobSubmitInfoModel);
          }
          return;
        }

        setJobParameter(params);

        setLoading(true);

        const { code, data } = await getUserInfoApi();

        if (code === 200) {
          setUserType("registered");
          setUsername(data.username);
          getTokenInfo(params);
        } else {
          setUserType("anonymous");
          setUsername(undefined);
        }
        setLoading(false);
        setLoginVisible(true);
      } catch (e) {
        if (e instanceof AxiosError) {
          setUserType("anonymous");
          setUsername(undefined);
          setLoginVisible(true);
        }

        setLoading(false);
      }
    }

    function onSubmit() {
      submitJob();
    }

    function onLoginCancel() {
      setLoginVisible(false);
    }

    function onLoginSubmit(job_name?: string) {
      if (!jobParameter || !job_name) {
        return;
      }

      const p = {
        ...jobParameter,
        job_name,
      };

      setLoading(true);

      createDockingJobApi(p)
        .then(({ code, data, message }) => {
          setLoading(false);
          if (code === 200) {
            onLoginCancel();
            const jobSubmitedInfo =
              getLocalStorage<JobSubmitInfoModel>("is_job_submited");
            if (!jobSubmitedInfo) {
              dispatch(setIsJobSubmited(true));
              setLocalStorage("is_job_submited", {
                submited: true,
              } as JobSubmitInfoModel);
            }
            Toast.success(message);
            navigate(`/job-detail/${data.job_id}`);
          }
        })
        .catch((e) => {
          setLoading(false);
        });
    }

    function onLogined() {
      if (!jobParameter) {
        return;
      }
      getTokenInfo(jobParameter);
    }

    function onToggleDeepThink() {
      setIsDeepThink(!isDeepThink);

      if (currentValue) {
        setValues(currentValue, !isDeepThink);
      }
    }

    function onValueChange(values: DockingAttachementModel) {
      setValues(values, isDeepThink);
      setCurrentValue(values);
    }

    const debouncedOnValueChange = useMemo(
      () => debounce(onValueChange, 500),
      [onValueChange],
    );

    function changeEntityList(
      cb: (input: SequenceRowModel[]) => SequenceRowModel[],
    ) {
      if (!formRef.current) {
        return;
      }

      const sequences = formRef.current.getValue("sequences");
      const output = cb(sequences);

      formRef.current.setValue("sequences", output);
    }

    function onDeleteRow(idx: number, key: string) {
      changeEntityList((sequences) => {
        const list = sequences.filter((item, i) => {
          return i !== idx;
        });

        return list;
      });
      delete inputRefs.current[key];
    }

    function onCollapseToggle(idx: number) {
      changeEntityList((sequences) => {
        const list = sequences.map((item, i) => {
          const o = { ...item };

          if (idx === i) {
            o.isCollapse = !o.isCollapse;
          }

          return o;
        });

        return list;
      });
    }

    function onValidate(v: boolean) {
      setConstraintsValid(v);
    }

    function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
      const content = e.clipboardData.getData("Text");
      const trimedContent = content.trim();
      const startStr = trimedContent[0];

      if (startStr === ">") {
        const res = processDockingFasta(content);

        if ("errorMsg" in res) {
          Toast.error(res.errorMsg);
        } else {
          const sequences = res.map((item, i) => {
            const o: SequenceRowModel = {
              copies: 1,
              sequence: replaceWhitespace(item.value),
              isEdit: false,
            };

            return o;
          });

          formRef.current?.setValue("sequences", sequences);
        }
      } else {
        Toast.error("Invalid fasta format");
      }
    }

    function onOpenProvideDialog(idx: number, row: SequenceRowModel) {
      if (visible) {
        return;
      }
      setProvideDialogVisible(true);
      setEditedIndex(idx);
      setEditedRow(row);
    }

    function onCancelProvide() {
      setProvideDialogVisible(false);
    }

    function onProvideReset(seq: string) {
      const idx = editedIndex;
      if (typeof idx !== "number") {
        return;
      }

      changeEntityList((sequences) => {
        const list = sequences.map((item, i) => {
          const o = { ...item };
          if (i === idx) {
            o.struct_condition_items = undefined;
            o.struct_condition_fid = undefined;
            o.pdb_input_type = undefined;
            o.pdb_input_value = undefined;
            o.sequence = seq;
          }

          return o;
        });

        return list;
      });

      setProvideDialogVisible(false);
    }

    function onProvideSubmit(data: ProvideStructureDataModel, fid: string) {
      const idx = editedIndex;
      if (typeof idx !== "number") {
        return;
      }

      changeEntityList((sequences) => {
        const list = sequences.map((item, i) => {
          const o = { ...item };
          if (i === idx) {
            o.sequence = data.entity_sequence || item.sequence;
            o.struct_condition_items = data.coord_items;
            o.pdb_input_type = data.input_type;
            o.pdb_input_value = data.input_value;
            o.struct_condition_fid = fid;
            o.copies = 1;
            o.isEdit = false;
          }

          return o;
        });

        return list;
      });

      formRef.current?.setError(`sequences[${idx}].sequence` as any, "");

      setProvideDialogVisible(false);
    }

    function formatText(item: SequenceRowModel) {
      if (item.pdb_input_type === VhhDesignTypeEnum.PdbID) {
        return `Ref: PDB ${item.pdb_input_value || ""}`;
      } else if (item.pdb_input_type === VhhDesignTypeEnum.UniProtID) {
        return `Ref: UniProt ${item.pdb_input_value || ""}`;
      } else if (item.pdb_input_type === VhhDesignTypeEnum.AntigenSequence) {
        return `Ref: ${item.pdb_input_value || ""}`;
      } else if (item.pdb_input_type === VhhDesignTypeEnum.UploadStructure) {
        return `Ref: ${item.pdb_input_value || ""}`;
      } else {
        return "-";
      }
    }

    function onDeleteCorrdItem(idx: number, k: number) {
      changeEntityList((sequences) => {
        const list = sequences.map((item, i) => {
          const o = { ...item };

          if (idx === i) {
            o.struct_condition_items = (o.struct_condition_items || []).filter(
              (_, j) => {
                return k !== j;
              },
            );
          }

          return o;
        });

        return list;
      });
    }

    function setupGuide(sequences: SequenceRowModel[]) {
      const steps: StepItem[] = [];
      const targetIndex = sequences.findIndex((item) => {
        return item.struct_condition_fid;
      });

      if (targetIndex !== -1) {
        const target = sequences[targetIndex];
        const stepOne: StepItem = {
          target: () =>
            document.querySelector(
              `#${target.struct_condition_fid!}-${targetIndex}`,
            )!,
          description: t("homePage.ab_ag_dock_lite.guide.stepOneMsg"),
          position: "bottomLeft",
          showArrow: false,
        };

        steps.push(stepOne);

        const stepTwo: StepItem = {
          target: () => document.querySelector(`#sequence-${targetIndex}`)!,
          description: t("homePage.ab_ag_dock_lite.guide.stepTwoMsg"),
          position: "bottomLeft",
          showArrow: false,
        };

        steps.push(stepTwo);
      } else {
        const stepOne: StepItem = {
          target: () => document.querySelector(`#provide-button-0`)!,
          description: t("homePage.ab_ag_dock_lite.guide.stepOneMsg"),
          position: "bottomLeft",
          showArrow: false,
        };

        steps.push(stepOne);
      }

      const stepThree: StepItem = {
        target: () => document.querySelector(`#docking-constraints-button`)!,
        description: t("homePage.ab_ag_dock_lite.guide.stepThreeMsg"),
        position: "topRight",
        showArrow: true,
      };

      steps.push(stepThree);

      setGuideSteps(steps);

      setTimeout(() => {
        setCurrent(0);
        showDialog();
      }, 100);
    }

    useEffect(() => {
      if (value) {
        setIsDeepThink(value.turbo);
        setTimeout(() => {
          formRef.current?.setValue("sequences", value.sequences);
          formRef.current?.setValue("constraints", value.constraints);
          // setupGuide(value.sequences);
        }, 100);
      }
    }, [JSON.stringify(value)]);

    useImperativeHandle(ref, () => ({
      setupGuide(sequences: SequenceRowModel[]) {
        setupGuide(sequences);
      },
    }));

    return (
      <>
        <Form
          className={styles.container}
          getFormApi={(formApi) => (formRef.current = formApi)}
          onValueChange={debouncedOnValueChange}
        >
          {({
            formState,
          }: {
            formState: FormState<DockingAttachementModel>;
          }) => {
            return (
              <ArrayField field="sequences" initValue={initSequencesData}>
                {({ arrayFields, addWithInitValue }) => (
                  <>
                    <div className="sequence-input-container appear-animate">
                      <div className="sequence-header">
                        <div
                          className="num-column"
                          style={{ flex: "0 0 210px" }}
                        >
                          {t("homePage.ab_ag_dock_lite.table.header.chain")}
                        </div>
                        <div className="name-column">
                          {t("homePage.ab_ag_dock_lite.table.header.copy")}
                        </div>
                        <div className="sequence-column">
                          {t("homePage.ab_ag_dock_lite.table.header.sequence")}
                        </div>
                        <div className="operate-column"></div>
                      </div>
                      <div className="sequence-body">
                        {arrayFields.map(({ field, key, remove }, i) => {
                          const sequences = formState.values?.sequences;
                          const sequence = sequences?.[i];
                          const constraints = formState.values?.constraints;
                          let chainName: string | undefined = undefined;
                          if (sequences) {
                            chainName = getChainIds(
                              sequences,
                              i,
                              sequence ? sequence.copies : 0,
                            );
                          }
                          const hotsportMap = getDockingHotspotResidueMap(
                            constraints,
                            chainName,
                          );
                          const map = getProvideResidueMap(
                            sequences?.[i]?.struct_condition_items,
                          );

                          return (
                            <React.Fragment key={key}>
                              <div className="sequence-content appear-animate">
                                {sequences && sequences[i] && (
                                  <div className="content-input-row">
                                    <div
                                      className="num-column"
                                      style={{ flex: "0 0 210px" }}
                                    >
                                      {chainName}
                                      <div>
                                        {sequences[i].struct_condition_fid ? (
                                          <Text
                                            disabled={visible}
                                            className={styles.provideText}
                                            onClick={() =>
                                              onOpenProvideDialog(
                                                i,
                                                sequences[i],
                                              )
                                            }
                                            style={{ width: 156 }}
                                            ellipsis
                                            id={`${sequences[i].struct_condition_fid}-${i}`}
                                          >
                                            {formatText(sequences[i])}
                                          </Text>
                                        ) : (
                                          <Text
                                            disabled={visible}
                                            // underline
                                            className={styles.provideText}
                                            onClick={() =>
                                              onOpenProvideDialog(
                                                i,
                                                sequences[i],
                                              )
                                            }
                                            id={`provide-button-${i}`}
                                          >
                                            ↑
                                            {t(
                                              "homePage.ab_ag_dock_lite.table.operation.provide",
                                            )}
                                          </Text>
                                        )}
                                      </div>
                                    </div>
                                    <div className="name-column">
                                      <InputNumber
                                        disabled={
                                          sequences[i].struct_condition_fid
                                            ? true
                                            : false
                                        }
                                        innerButtons={true}
                                        field={`${field}[copies]`}
                                        noLabel
                                        min={1}
                                        max={4}
                                        style={{ width: 72 }}
                                      />
                                    </div>
                                    {sequences[i] && (
                                      <div className="sequence-column">
                                        <div
                                          className={classNames({
                                            [styles.seqWrapper]: true,
                                            [styles.hidden]:
                                              !sequences[i].isCollapse,
                                          })}
                                        >
                                          <Text
                                            ellipsis={true}
                                            style={{
                                              width: 224,
                                              color: "var(--semi-color-text-2)",
                                            }}
                                          >
                                            {sequences[i].sequence}
                                          </Text>
                                          (length{" "}
                                          {
                                            replaceWhitespace(
                                              sequences[i].sequence,
                                            ).length
                                          }
                                          )
                                        </div>
                                        <div
                                          className={classNames({
                                            "sequence-container": true,
                                            [styles.hidden]:
                                              sequences[i].isEdit ||
                                              sequences[i].isCollapse,
                                          })}
                                          onPointerDown={(e) =>
                                            onEditSeq(e, key, field)
                                          }
                                          id={`sequence-${i}`}
                                        >
                                          <div className="seq-wrapper">
                                            {parseCCDSequence(
                                              sequences[i].sequence,
                                            ).map((residue, index) => {
                                              return (
                                                <>
                                                  {residue.includes("(") ? (
                                                    <>
                                                      {residue
                                                        .split("")
                                                        .map((ch, k) => {
                                                          return (
                                                            <span
                                                              className="seq"
                                                              key={`${index}-${k}`}
                                                              style={{
                                                                backgroundColor:
                                                                  map[index + 1]
                                                                    ? "var(--semi-color-primary-light-hover)"
                                                                    : "",
                                                                color:
                                                                  hotsportMap[
                                                                    index + 1
                                                                  ]
                                                                    ? "#F01D94"
                                                                    : "",
                                                              }}
                                                            >
                                                              {ch}

                                                              {index % 10 ===
                                                                0 &&
                                                                k === 0 && (
                                                                  <span
                                                                    className="label"
                                                                    style={{
                                                                      visibility:
                                                                        "visible",
                                                                    }}
                                                                  >
                                                                    {index + 1}
                                                                  </span>
                                                                )}
                                                            </span>
                                                          );
                                                        })}
                                                    </>
                                                  ) : (
                                                    <span
                                                      className="seq"
                                                      key={index}
                                                      style={{
                                                        backgroundColor: map[
                                                          index + 1
                                                        ]
                                                          ? "var(--semi-color-primary-light-hover)"
                                                          : "",
                                                        color: hotsportMap[
                                                          index + 1
                                                        ]
                                                          ? "#F01D94"
                                                          : "",
                                                      }}
                                                    >
                                                      {residue}

                                                      {index % 10 === 0 && (
                                                        <span
                                                          className="label"
                                                          style={{
                                                            visibility:
                                                              "visible",
                                                          }}
                                                        >
                                                          {index + 1}
                                                        </span>
                                                      )}
                                                    </span>
                                                  )}
                                                </>
                                              );
                                            })}
                                          </div>
                                        </div>

                                        <TextArea
                                          rows={3}
                                          field={`${field}[sequence]`}
                                          noLabel
                                          validate={(v: string) =>
                                            ccdCodeValidate(v)
                                          }
                                          className="sequence-textarea"
                                          trigger="blur"
                                          fieldClassName={classNames({
                                            [styles.hidden]:
                                              !sequences[i].isEdit ||
                                              sequences[i].isCollapse,
                                          })}
                                          ref={(refer: HTMLTextAreaElement) => {
                                            inputRefs.current[key] = refer;
                                          }}
                                          onBlur={() => onSeqBlur(field)}
                                          onPaste={onPaste}
                                          placeholder={t(
                                            "homePage.ab_ag_dock_lite.table.placeholder",
                                          )}
                                        />
                                        {sequences[i]
                                          .struct_condition_items && (
                                          <div>
                                            {sequences[
                                              i
                                            ].struct_condition_items.map(
                                              (item, k) => {
                                                return (
                                                  <Tag
                                                    key={k}
                                                    closable
                                                    className={styles.tag}
                                                    onClose={(v, e) => {
                                                      e.preventDefault();
                                                      onDeleteCorrdItem(i, k);
                                                    }}
                                                  >
                                                    {item.struct_segment} → seq{" "}
                                                    {item.seq_segment}
                                                  </Tag>
                                                );
                                              },
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    <div className="operate-column">
                                      <Dropdown
                                        clickToHide
                                        position={"bottomRight"}
                                        render={
                                          <Dropdown.Menu>
                                            <Dropdown.Item
                                              icon={<DeleteIcon />}
                                              disabled={arrayFields.length <= 1}
                                              onClick={() =>
                                                onDeleteRow(i, key)
                                              }
                                            >
                                              {t(
                                                "homePage.ab_ag_dock_lite.table.operation.delete",
                                              )}
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                              disabled={
                                                replaceWhitespace(
                                                  sequences[i].sequence,
                                                ).length <= 0
                                              }
                                              icon={
                                                sequences[i].isCollapse ? (
                                                  <MaximizeIcon />
                                                ) : (
                                                  <MinimizeIcon />
                                                )
                                              }
                                              onClick={() =>
                                                onCollapseToggle(i)
                                              }
                                            >
                                              {sequences[i].isCollapse
                                                ? t(
                                                    "homePage.ab_ag_dock_lite.table.operation.expand",
                                                  )
                                                : t(
                                                    "homePage.ab_ag_dock_lite.table.operation.collapse",
                                                  )}
                                            </Dropdown.Item>
                                          </Dropdown.Menu>
                                        }
                                      >
                                        <IconMore className={styles.iconMore} />
                                      </Dropdown>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                      <div className="sequence-footer">
                        <div className="left">
                          <Button
                            theme="light"
                            type="tertiary"
                            size="small"
                            disabled={arrayFields.length >= 6}
                            className="border-btn"
                            icon={<IconPlus />}
                            onPointerDown={() => {
                              if (arrayFields.length >= 6) {
                                return;
                              }
                              addWithInitValue({
                                copies: 1,
                                sequence: "",
                                isEdit: true,
                              });
                            }}
                          >
                            {t("homePage.ab_ag_dock_lite.table.operation.add")}
                          </Button>

                          <Tooltip
                            content={t(
                              "homePage.ab_ag_dock_lite.table.operation.modeTooltip",
                            )}
                          >
                            <Button
                              theme="borderless"
                              type="tertiary"
                              size="small"
                              onClick={onToggleDeepThink}
                              className={classNames({
                                "border-btn": true,
                                active: isDeepThink,
                              })}
                              icon={<BulbIcon />}
                              style={{ marginLeft: "16px" }}
                            >
                              {t(
                                "homePage.ab_ag_dock_lite.table.operation.mode",
                              )}
                            </Button>
                          </Tooltip>
                        </div>
                        <div className="right">
                          <DockingAdvancedSettingField
                            noLabel
                            field="constraints"
                            fieldStyle={{
                              padding: 0,
                              lineHeight: "0",
                            }}
                            isTuition={visible}
                            ref={constraintRef}
                            sequences={formState.values?.sequences}
                            onValidate={onValidate}
                          />
                          <Button
                            theme="solid"
                            type="primary"
                            size="small"
                            // htmlType="submit"
                            className={styles.searchBtn}
                            style={{ marginLeft: "8px" }}
                            loading={loading}
                            onClick={onSubmit}
                            icon={<IconSearch />}
                          ></Button>
                          <span
                            style={{
                              display: "inline-block",
                              width: "16px",
                            }}
                          ></span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </ArrayField>
            );
          }}
        </Form>

        <UserGuide
          className={styles.userGuideContainer}
          mode="popup"
          mask={true}
          theme="primary"
          visible={visible}
          current={current}
          steps={guideSteps}
          showPrevButton={false}
          finishText={t("homePage.ab_ag_dock_lite.guide.done")}
          onChange={(step) => {
            setCurrent(step);

            if (step + 1 === guideSteps.length) {
              constraintRef.current?.openPopover();
            }
          }}
          nextButtonProps={{
            size: "small",
          }}
          onFinish={() => {
            setVisible(false);
            setCurrent(0);
          }}
          onSkip={() => {
            setVisible(false);
            setCurrent(0);
          }}
        />

        {loginVisible && (
          <JobLoginDialog
            cancel={onLoginCancel}
            submit={onLoginSubmit}
            logined={onLogined}
            type={userType}
            tokenInfo={tokenInfo}
            username={username}
            loading={loadingInfo}
            submitingJob={loading}
            // type="anonymous"
          />
        )}

        {provideDialogVisible && editedRow && (
          <DockingProvideStructureDialog
            row={editedRow}
            cancel={onCancelProvide}
            submit={(data: ProvideStructureDataModel, fid: string) =>
              onProvideSubmit(data, fid)
            }
            reset={onProvideReset}
          />
        )}
      </>
    );
  },
);

DockingTableField.displayName = "DockingTableField";
export default withField(DockingTableField);
